import type { FirmFile } from "@/lib/types";
import { getFileNameParts } from "@/lib/utils/matterFile";

type FirmFileLike = Pick<
  FirmFile,
  | "dateCreated"
  | "dateModified"
  | "id"
  | "idLocation"
  | "isFolder"
  | "name"
  | "presignedUrlKey"
>;

export type FirmFileSortKey = "dateModified" | "dateCreated" | "name";

export const getFirmFileExtension = (
  file: Pick<FirmFile, "name" | "presignedUrlKey" | "isFolder">,
) => {
  if (file.isFolder) {
    return "";
  }

  const fromName = getFileNameParts(file.name).fileExtension;
  if (fromName) {
    return fromName;
  }

  const keyName = file.presignedUrlKey?.split("/").pop();
  if (!keyName) {
    return "";
  }

  return getFileNameParts(keyName).fileExtension;
};

export const getFirmFileBaseName = (
  file: Pick<FirmFile, "name" | "isFolder">,
) => {
  if (file.isFolder) {
    return file.name;
  }

  const { fileName, fileExtension } = getFileNameParts(file.name);
  return fileExtension ? fileName : file.name;
};

export const getFirmFileDisplayName = (
  file: Pick<FirmFile, "name" | "presignedUrlKey" | "isFolder">,
) => {
  if (file.isFolder) {
    return file.name;
  }

  const { fileExtension } = getFileNameParts(file.name);
  if (fileExtension) {
    return file.name;
  }

  const extension = getFirmFileExtension(file);
  return extension ? `${file.name}.${extension}` : file.name;
};

type FirmFileNameComparable = Pick<
  FirmFile,
  "isFolder" | "name" | "presignedUrlKey"
>;

export const getFirmFileComparableName = (file: FirmFileNameComparable) =>
  file.isFolder ? file.name : getFirmFileDisplayName(file);

export const firmFileDisplayNameExistsInFolder = (
  files: Pick<
    FirmFile,
    "id" | "idLocation" | "isFolder" | "name" | "presignedUrlKey"
  >[],
  folderId: string,
  displayName: string,
  excludeFirmFileId?: string,
) => {
  const normalized = displayName.trim().toLowerCase();

  return files.some((item) => {
    if (item.id === excludeFirmFileId) {
      return false;
    }

    if ((item.idLocation || "") !== (folderId || "")) {
      return false;
    }

    return getFirmFileComparableName(item).trim().toLowerCase() === normalized;
  });
};

export const getFirmFileDisplayNamesInFolder = (
  files: Pick<
    FirmFile,
    "idLocation" | "isFolder" | "name" | "presignedUrlKey"
  >[],
  folderId: string,
) =>
  files
    .filter((file) => (file.idLocation || "") === (folderId || ""))
    .map((file) => getFirmFileComparableName(file));

export const sortFirmFiles = <T extends FirmFileLike>(
  items: readonly T[],
  sortKey: FirmFileSortKey,
) => {
  const getTime = (value?: string) => (value ? new Date(value).getTime() : 0);
  const collator = new Intl.Collator(undefined, {
    sensitivity: "base",
    numeric: true,
  });

  return [...items].sort((a, b) => {
    switch (sortKey) {
      case "name":
        return collator.compare(
          getFirmFileDisplayName(a),
          getFirmFileDisplayName(b),
        );
      case "dateCreated":
        return getTime(b.dateCreated) - getTime(a.dateCreated);
      case "dateModified":
      default:
        return getTime(b.dateModified) - getTime(a.dateModified);
    }
  });
};

export const buildFirmFileBreadcrumbs = (
  files: Pick<FirmFile, "id" | "idLocation" | "isFolder" | "name">[],
  currentFolderId: string,
) => {
  const folderMap = new Map(
    files.filter((file) => file.isFolder).map((file) => [file.id, file]),
  );

  const breadcrumbs: Pick<
    FirmFile,
    "id" | "idLocation" | "isFolder" | "name"
  >[] = [];
  let cursor = currentFolderId;

  while (cursor) {
    const folder = folderMap.get(cursor);
    if (!folder) {
      break;
    }
    breadcrumbs.unshift(folder);
    cursor = folder.idLocation || "";
  }

  return breadcrumbs;
};

export const isFirmFolderDescendant = (
  files: Pick<FirmFile, "id" | "idLocation" | "isFolder">[],
  folderId: string,
  potentialDescendantId: string,
) => {
  if (!folderId || !potentialDescendantId) {
    return false;
  }

  const folderMap = new Map(
    files.filter((file) => file.isFolder).map((file) => [file.id, file]),
  );

  let cursor = potentialDescendantId;
  while (cursor) {
    if (cursor === folderId) {
      return true;
    }

    const folder = folderMap.get(cursor);
    if (!folder) {
      break;
    }
    cursor = folder.idLocation || "";
  }

  return false;
};
